"use client";

import { reportError } from "@/actions/error-report";
import { Button } from "@heroui/react";
import { useTranslations } from "next-intl";
import { useEffect } from "react";

function isChunkError(message: string): boolean {
  return (
    message.includes("module factory is not available") ||
    message.includes("Loading chunk") ||
    message.includes("ChunkLoadError")
  );
}

function isHydrationError(message: string, stack?: string): boolean {
  // Detect React hydration and DOM manipulation errors
  // These can occur when cached HTML doesn't match new JS chunks
  return (
    message.includes("The object can not be found here") ||
    message.includes("Hydration failed") ||
    message.includes("There was an error while hydrating") ||
    message.includes("Text content does not match") ||
    (stack?.includes("insertBefore") ?? false) ||
    (stack?.includes("removeChild") ?? false)
  );
}

function isRecoverableError(message: string, stack?: string): boolean {
  return isChunkError(message) || isHydrationError(message, stack);
}

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const t = useTranslations();

  useEffect(() => {
    console.error("Error boundary caught:", error);

    const message = error.message || "";
    const stack = error.stack || "";

    const isChunk = isChunkError(message);
    const isHydration = isHydrationError(message, stack);
    const isRecoverable = isRecoverableError(message, stack);
    const reloadKey = "error-reload-" + window.location.pathname;
    const alreadyReloaded = sessionStorage.getItem(reloadKey);

    // Build report message with error type status
    let reportMessage = error.message || "Unknown error";
    if (isRecoverable) {
      const errorType = isChunk ? "Chunk Error" : "Hydration Error";
      reportMessage = alreadyReloaded
        ? `[${errorType} - Reload Failed] ${reportMessage}`
        : `[${errorType} - Reloading] ${reportMessage}`;
    }

    // Deduplicate error reports across remounts using sessionStorage
    const reportKey =
      "error-reported-" +
      (isRecoverable ? (alreadyReloaded ? "post-" : "pre-") : "") +
      (error.digest || error.message);
    if (!sessionStorage.getItem(reportKey)) {
      sessionStorage.setItem(reportKey, "1");
      reportError({
        message: reportMessage,
        stack: error.stack,
        digest: error.digest,
        url: typeof window !== "undefined" ? window.location.href : "unknown",
        userAgent:
          typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
      }).catch(console.error);
    }

    // Recoverable errors (chunk/hydration): auto-reload with cache-buster after reporting
    if (isRecoverable && !alreadyReloaded) {
      sessionStorage.setItem(reloadKey, "1");
      const url = new URL(window.location.href);
      url.searchParams.set("_r", Date.now().toString());
      window.location.replace(url.toString());
      return;
    }
  }, [error]);

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] gap-6 p-8">
      <div className="text-center space-y-3">
        <h2 className="text-2xl font-bold text-white/90">
          {t("common.error.title")}
        </h2>
        <p className="text-white/60 max-w-md">
          {t("common.error.description")}
        </p>
        {error.digest && (
          <p className="text-xs text-white/40 font-mono">
            Error ID: {error.digest}
          </p>
        )}
      </div>
      <div className="flex gap-3">
        <Button
          onPress={() => reset()}
          className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30"
        >
          {t("common.error.tryAgain")}
        </Button>
        <Button
          as="a"
          href="/"
          variant="flat"
          className="text-white/60 hover:text-white"
        >
          {t("common.error.goHome")}
        </Button>
      </div>
    </div>
  );
}
